/* eslint-disable import/no-cycle */
import { ITEM_STATUS_OPTIONS } from "@shared/constants";
import { DataProvider } from "@shared/constants/common";
import UserBasedWaitlistSubscriberRepository from "@app-contexts/user/features/waitlists/data/repositories/UserBasedWaitlistSubscriberRepository";
import { PaginatorContract } from "@shared/services/api/types";
import { UserBasedWaitlistSubscriberSerializer } from "@app-contexts/user/features/waitlists/data/types/user-waitlist-types";
import { PAYMENT_MANAGER_CONFIRMATION_STRATEGY } from "@shared/modules/payments/utils/manager/utils";

import { PaymentConfigurationError } from "../../utils/errors/PaymentConfigurationError";
import { PaymentInitializationError } from "../../utils/errors/PaymentInitializationError";
import { PaymentProcessingError } from "../../utils/errors/PaymentProcessingError";

import StripePaymentGateway from "../../gateways/StripePaymentGateway/StripePaymentGateway";
import PaymentGatewayFactory from "../../PaymentGatewayFactory";
import useBasketPaymentService, {
  PaymentServiceBasket,
} from "../../services/useBasketPaymentService";
import useInvoicePaymentService from "../../services/useInvoicePaymentService";
import PaymentHandler from "../PaymentHandler";
import ShowpassPaymentGateway from "../../gateways/ShowpassPaymentGateway/ShowpassPaymentGateway";
import { PurchaseOptsType } from "../../types";
import { type PaymentManagerConfig } from "../../PaymentManager";
import PaymentGateway from "../../gateways/PaymentGateway";

/**
 * The default payment handler for Waitlist payments
 */
class WaitlistPaymentHandler extends PaymentHandler {
  public static handlerName = "WaitlistPaymentHandler";

  HAS_ASYNCHRONOUS_CONFIRMATION = true;
  HAS_ASYNCHRONOUS_CAPTURE = true;

  is_initialized = false;
  // Waitlists with cc_verification_required=True use Stripe
  // Waitlists with cc_verifictaion_required=False use ShowpassPaymentGateway.
  // $0 Waitlists will always use ShowpassPaymentGateway.
  gateway: StripePaymentGateway | ShowpassPaymentGateway | null = null;
  waitlistRepository: UserBasedWaitlistSubscriberRepository | null = null;

  constructor(
    config: PaymentManagerConfig,
    basket: PaymentServiceBasket,
    servicesContext: {
      basketPaymentService: ReturnType<typeof useBasketPaymentService>;
      invoicePaymentService: ReturnType<typeof useInvoicePaymentService>;
    }
  ) {
    super(config, basket, servicesContext);

    const selectedGateway = PaymentGatewayFactory.getOrCreateGateway(
      config,
      basket,
      servicesContext
    );

    // Check that the gateway that was selected is supported by this handler
    if (
      !(selectedGateway instanceof StripePaymentGateway) &&
      !(selectedGateway instanceof ShowpassPaymentGateway)
    ) {
      throw new PaymentConfigurationError(
        `Waitlist payment handler only supports Stripe and Showpass payment gateways, but received ${selectedGateway.constructor.name}. Please check your waitlist payment configuration.`,
        {
          metadata: {
            supportedGateways: [
              "StripePaymentGateway",
              "ShowpassPaymentGateway",
            ],
            receivedGateway: selectedGateway.constructor.name,
          },
        }
      );
    }

    this.gateway = selectedGateway;
  }

  async initialize(basket: PaymentServiceBasket) {
    if (!this.gateway) {
      throw new PaymentInitializationError(
        "Cannot initialize waitlist payment handler because the payment gateway is not set. This indicates a configuration issue.",
        {
          metadata: {
            operation: "initialize",
          },
        }
      );
    }

    if (this.gateway instanceof ShowpassPaymentGateway) {
      await this.gateway.initialize();
    } else {
      await this.gateway.initialize(basket, {
        mode: "setup",
      });
    }

    this.is_initialized = true;
    return this;
  }

  /**
   * Process the checkout for waitlists
   */
  async processCheckout(
    confirmationStrategy: PAYMENT_MANAGER_CONFIRMATION_STRATEGY,
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType
  ) {
    if (!this.gateway) {
      throw new PaymentProcessingError(
        "Cannot process waitlist payment because the payment gateway is not initialized",
        {
          metadata: {
            operation: "processCheckout",
          },
        }
      );
    }

    if (this.gateway instanceof ShowpassPaymentGateway) {
      await this.gateway.confirmPayment(
        confirmationStrategy,
        basketType,
        basket,
        purchaseOpts
      );
    } else if (this.gateway instanceof StripePaymentGateway) {
      // Save the payment method for off-session payment confirmation when a waitlisted basket can be converted
      await this.gateway.savePaymentMethod(basketType, basket);

      // Confirm that this basket's payment will be processed at an arbitrary time in the future
      await this.gateway.confirmDeferredPayment(
        confirmationStrategy,
        basketType,
        basket,
        purchaseOpts
      );
    } else {
      throw new PaymentProcessingError(
        "Cannot process waitlist payment because the gateway is not a supported type for this handler",
        {
          metadata: {
            operation: "processCheckout",
            gatewayType:
              (this.gateway as PaymentGateway)?.constructor?.name || "unknown",
          },
        }
      );
    }

    // Add other gateways here

    return this;
  }

  /**
   * Post-purchase logic for waitlist payments
   *
   * Where most handlers + gateways poll for invoice, we poll for basket status as
   * waitlist baskets do not generate an invoice on payment method capture.
   */
  async postProcessCheckout() {
    /* eslint-disable no-await-in-loop -- https://eslint.org/docs/latest/rules/no-await-in-loop#when-not-to-use-it */
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    if (!this.basketPaymentService) {
      throw new PaymentProcessingError(
        "Cannot complete waitlist payment processing because the basket payment service is not available",
        {
          metadata: {
            operation: "postProcessCheckout",
            missingService: "basketPaymentService",
          },
        }
      );
    }

    // Skip gateway postProcessCheckout. Waitlists currently only support StripePaymentGateways,
    // for which we do not need post checkout processing when handling waitlists.

    // Poll for the basket until we get a waitlisted status
    // Set above 5 for async payment flows to account for webhook delays
    const MAX_BASKET_ATTEMPTS = 10;
    let basketCandidate;
    let finalBasket;

    for (let attempts = 1; attempts <= MAX_BASKET_ATTEMPTS; attempts++) {
      basketCandidate = await this.basketPaymentService.refresh({
        no_lock: true,
      });

      if (basketCandidate?.id == null) {
        throw new PaymentProcessingError(
          "Unable to retrieve basket information after waitlist payment",
          {
            metadata: {
              operation: "postProcessCheckout",
              attempts,
              maxAttempts: MAX_BASKET_ATTEMPTS,
            },
          }
        );
      }

      // If we have a waitlisted basket, break out of the polling loop
      if (basketCandidate.status === ITEM_STATUS_OPTIONS.WAITLISTED.id) {
        finalBasket = basketCandidate;
        break;
      }

      // Wait before trying again if we haven't found our basket state yet
      if (attempts < MAX_BASKET_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }

    // If we couldn't get a waitlisted status after all attempts, throw an error
    if (finalBasket == null || finalBasket.id == null) {
      throw new PaymentProcessingError(
        "Waitlist payment could not be completed. The basket did not reach waitlisted status after maximum attempts",
        {
          metadata: {
            operation: "postProcessCheckout",
            expectedStatus: "waitlisted",
            maxAttempts: MAX_BASKET_ATTEMPTS,
          },
        }
      );
    }

    await this.getWaitlist(finalBasket.id);

    return finalBasket;
  }

  /**
   * Poll for waitlist subscribers associated with a basket
   *
   * @param basketId - The ID of the basket to check for waitlist subscribers
   * @returns The waitlist subscriber data
   * @throws Error if waitlist subscriber data can't be found after maximum attempts
   */
  private async getWaitlist(
    basketId: string | number
  ): Promise<PaginatorContract<UserBasedWaitlistSubscriberSerializer>> {
    /* eslint-disable no-await-in-loop -- https://eslint.org/docs/latest/rules/no-await-in-loop#when-not-to-use-it */

    const MAX_WAITLIST_ATTEMPTS = 5;
    const waitlistRepository = new UserBasedWaitlistSubscriberRepository();

    let waitlistResponse = null;
    let finalWaitlist = null;

    for (let attempts = 1; attempts <= MAX_WAITLIST_ATTEMPTS; attempts++) {
      // Make a paginated request with the basket ID as a query parameter
      waitlistResponse = await waitlistRepository.paginate({
        params: { basket: basketId },
      });

      // If we have waitlist data, break out of the polling loop
      if (
        waitlistResponse &&
        waitlistResponse.results &&
        waitlistResponse.results.length > 0
      ) {
        // Store the repository reference for future use within this instance
        this.waitlistRepository = waitlistRepository;
        finalWaitlist = waitlistResponse;
        break;
      }

      // Wait before trying again if we haven't found our waitlist data yet
      if (attempts < MAX_WAITLIST_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }

    // If we couldn't get a waitlist after all attempts, throw an error
    if (finalWaitlist == null) {
      throw new PaymentProcessingError(
        "Unable to retrieve waitlist information after payment",
        {
          metadata: {
            operation: "getWaitlist",
            maxAttempts: MAX_WAITLIST_ATTEMPTS,
          },
        }
      );
    }

    return finalWaitlist;
  }
}

export default WaitlistPaymentHandler;
